import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse } from './baseApi';

/**
 * Scrap report row DTO (process-level detail from backend).
 * Matches the structure returned by scrap report date-range / details APIs.
 */
export interface ScrapReportRowDto {
  reportDate: string;
  processStepId: number;
  processName: string;
  sequenceOrder: number;
  workOrderOperationId: number;
  quantityCompleted: number;
  outputWeightPerUnit: number;
  totalOutputWeight: number;
  inputWeightPerUnit: number;
  totalInputWeight: number;
  scrapWeight: number;
  productionDate: string;
  productionDateFormatted: string;
  productionYear: number;
  productionMonth: number;
  productionDay: number;
  branchId: number;
  branchName: string;
  branchCode: string;
  workOrderId: number;
  workOrderNumber: string;
  endgoodId: number;
  endgoodName: string;
  endgoodSku: string;
  endgoodPartNumber: string;
  endgoodProductionWeightGrams: number;
  bomHeaderId: number;
  bomNumber: string;
  bomName: string;
  bomProductionQuantity: number;
  rawItemId: number;
  rawItemName: string;
  rawItemSku: string;
  bomDetailId: number;
  bomDetailQuantityTonsPerPart: number;
  totalRejectQuantity: number;
  totalCompletedQuantity: number;
  totalMaterialIssued: number;
  rejectScrapTons: number;
  materialScrapTons: number;
}

// Scrap report DTOs - Details use the same structure as DailyScrapSummaryDto
export interface ScrapDetailDto extends DailyScrapSummaryDto {}

export interface DailyScrapSummaryDto {
  productionDate: string;
  productionDateFormatted: string;
  productionYear: number;
  productionMonth: number;
  productionDay: number;
  branchId: number;
  branchName: string;
  branchCode: string;
  workOrderId?: number;
  workOrderNumber?: string;
  endgoodId?: number;
  endgoodName?: string;
  endgoodSku?: string;
  endgoodPartNumber?: string;
  endgoodProductionWeightGrams?: number;
  bomHeaderId?: number;
  bomNumber?: string;
  bomName?: string;
  bomProductionQuantity?: number;
  rawItemId?: number;
  rawItemName?: string;
  rawItemSku?: string;
  bomDetailId?: number;
  bomDetailQuantityTonsPerPart?: number;
  totalRejectQuantity?: number;
  totalCompletedQuantity?: number;
  materialIssueCount?: number;
  totalMaterialIssued: number;
  materialIssueNumbers?: string | null;
  materialIssueIds?: string | null;
  totalMaterialConsumed?: number | null;
  totalMaterialReturned?: number | null;
  totalMaterialWasted?: number | null;
  rejectScrapTons: number;
  materialScrapTons: number;
  scrapPercentage: number;
  // Process-level fields (when API returns ScrapReportRowDto shape)
  reportDate?: string;
  processStepId?: number;
  processName?: string;
  sequenceOrder?: number;
  workOrderOperationId?: number;
  quantityCompleted?: number;
  outputWeightPerUnit?: number;
  totalOutputWeight?: number;
  inputWeightPerUnit?: number;
  totalInputWeight?: number;
  scrapWeight?: number;
  // Legacy fields for backward compatibility
  totalRejectScrapTons?: number;
  totalMaterialScrapTons?: number;
  totalScrapTons?: number;
  totalWorkOrders?: number;
  totalEndgoods?: number;
  totalRawItems?: number;
}

export interface ScrapDailySummaryResponseDto extends Array<DailyScrapSummaryDto> {}

export interface ScrapDetailsResponseDto {
  details: ScrapDetailDto[];
}

// Scrap Report API Service
class ScrapReportApiService extends BaseApiService {
  
  // Get scrap report data by date range
  async getScrapReportByDateRange(
    startDate: string,
    endDate: string,
    branchId?: number
  ): Promise<ApiResponse<DailyScrapSummaryDto[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<DailyScrapSummaryDto[]>>(
        API_ENDPOINTS.SCRAP_REPORTS.DATE_RANGE(startDate, endDate, branchId),
        { method: HTTP_METHODS.GET }
      );
      
      return {
        success: response.success ?? true,
        message: response.message || 'Scrap report fetched successfully',
        data: Array.isArray(response.data) ? response.data : []
      };
    } catch (error) {
      // console.error('ScrapReportApiService: Error fetching scrap report by date range:', error);
      toast.error('Failed to fetch scrap report');
      throw error;
    }
  }

  // Get daily scrap summary for current month (deprecated - use getScrapReportByDateRange instead)
  async getDailySummaryCurrentMonth(
    branchId: number
  ): Promise<ApiResponse<DailyScrapSummaryDto[]>> {
    try {
      // Backend returns ApiResponse format directly
      const response = await this.makeRequest<ApiResponse<DailyScrapSummaryDto[]>>(
        API_ENDPOINTS.SCRAP_REPORTS.DAILY_SUMMARY_CURRENT_MONTH(branchId),
        { method: HTTP_METHODS.GET }
      );
      
      // Return the response directly since backend already formats it as ApiResponse
      return {
        success: response.success ?? true,
        message: response.message || 'Scrap daily summary fetched successfully',
        data: Array.isArray(response.data) ? response.data : []
      };
    } catch (error) {
      // console.error('ScrapReportApiService: Error fetching daily summary:', error);
      toast.error('Failed to fetch scrap daily summary');
      throw error;
    }
  }

  // Get scrap details for a specific date
  async getScrapDetails(
    productionDate: string,
    branchId: number
  ): Promise<ApiResponse<ScrapDetailsResponseDto>> {
    try {
      const data = await this.makeRequest<ScrapDetailsResponseDto>(
        API_ENDPOINTS.SCRAP_REPORTS.DETAILS(productionDate, branchId),
        { method: HTTP_METHODS.GET }
      );
      
      return {
        success: true,
        message: 'Scrap details fetched successfully',
        data: data
      };
    } catch (error) {
      // console.error('ScrapReportApiService: Error fetching scrap details:', error);
      toast.error('Failed to fetch scrap details');
      throw error;
    }
  }
}

export const scrapReportApiService = new ScrapReportApiService();

